import { Switch } from '../../../../components';
import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    AudioPlayer,
    Card,
    Exercise,
    LabelBody,
    LabelBodyHeadline,
    LabelDisplay2,
    ProgressBar,
    SecondaryButton,
    Spacer,
    Textarea,
    Tip,
} from '../../components';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import { IllustrationPenPaper } from '../../icons';
import { ExerciseDto, ExerciseStatus } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';

const page1RelaxationTextKey = 'module2Exercise5Page1RelaxationText';

export const exerciseData26: ExerciseData = {
    ExerciseView: Module2Exercise6,
    dataKeys: [page1RelaxationTextKey],
    questions: [
        createQuestionFromConfiguration({
            title: 'Aktivér din bremse: Spænd op og spænd af',
            description: 'Hvad skete der med tankerne imens?',
            dataKeys: [page1RelaxationTextKey],
        }),
    ],
};

function Module2Exercise6({ exercise, onContinue }: ExerciseViewProps) {
    return (
        <Exercise title="Afspænding">
            <ProgressBar current={exercise.CurrentPage} total={2} />
            <Switch
                cases={{
                    1: (
                        <Page1
                            relaxationText={
                                exercise.Data[page1RelaxationTextKey] ?? ''
                            }
                            exercise={exercise}
                            onContinue={(relaxationText) => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                    data: {
                                        [page1RelaxationTextKey]:
                                            normalizeInput(relaxationText),
                                    },
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: false,
                                });
                            }}
                        />
                    ),
                }}
                value={exercise.CurrentPage}
                default={1}
            />
        </Exercise>
    );
}

type Page1Props = {
    relaxationText: string;
    exercise: ExerciseDto;
    onContinue: (relaxationText: string) => void;
};

function Page1({
    relaxationText: initialrelaxationText,
    exercise,
    onContinue,
}: Page1Props) {
    const [relaxationText, setRelaxationText] = useSyncedState<string>(
        initialrelaxationText,
    );

    return (
        <>
            <LabelDisplay2>
                Aktivér din bremse: Spænd op og spænd af
            </LabelDisplay2>
            <LabelBody class="mt-10">
                Denne øvelse går ud på at eksperimentere med at spænde op og
                spænde af i forskellige dele af din krop. Ved først at spænde op
                og derefter slappe af i en muskel, kan du få mere ro på både
                kroppen og tankerne. Find et uforstyrret sted og start
                lydklippet, når du er klar.
            </LabelBody>
            <AudioPlayer
                exercise={exercise}
                src="https://static.hejdoktor.dk/falcksundtsind/AFSPÆNDING+02+MIX.mp3"
                duration="00:12:11"
                type="audio/mpeg"
                className="my-30"
            >
                <LabelBodyHeadline>Afspænding</LabelBodyHeadline>
            </AudioPlayer>
            <Card>
                <IllustrationPenPaper />
                <LabelDisplay2 class="mb-10 mt-20">
                    Hvordan gik det?
                </LabelDisplay2>
                <LabelBody>Hvad skete der med tankerne imens?</LabelBody>
                <Textarea
                    placeholder="Skriv her..."
                    text={relaxationText}
                    onChangeText={setRelaxationText}
                    className="mb-10"
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton
                class="mt-30"
                onClick={() => onContinue(relaxationText)}
            >
                Fortsæt
            </SecondaryButton>
        </>
    );
}

type Page2Props = { onContinue: () => void };
function Page2({ onContinue }: Page2Props) {
    return (
        <>
            <LabelDisplay2>Et godt redskab</LabelDisplay2>
            <LabelBody class="my-10">
                Når vi igennem kroppen fortæller hjernen, at det er OK at give
                slip og slappe af, bliver det muligt at lade tankerne passere
                forbi. Det gør det muligt at være mere til stede.
            </LabelBody>
            <LabelBody>
                Afspændingsøvelsen kan give en afslappethed nu og her, men det
                kan være godt at øve sig flere gange. Over tid kan du blive mere
                bevidst om, hvornår du er anspændt, og vide at det kan hjælpe at
                spænde af.
            </LabelBody>
            <Tip className="my-30">
                Hvis du har svært ved at sove, kan teknikken være en hjælp, når
                du ligger i sengen.
            </Tip>
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
